import { Grid, NumberInput, Space } from '@mantine/core';
import { type Dispatch, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { query } from '../../../util/api/query';
import { buildFilterUrl } from '../../../util/convertToUrl';
import type { FilterDto } from '../../../util/types/FilterDto';
import type { StatDto } from '../../home/types/StatDto';
import { useCalculationForm } from '../hooks/useCalculationsForm';
import type { VatReturnInputs } from '../types/VatReturn';
import { ButtonOrCancel } from './VatReturnExportModal';

interface CalculationsFormProps {
	setSelectedPage: Dispatch<React.SetStateAction<number>>;
	setInputsInParent: Dispatch<React.SetStateAction<VatReturnInputs>>;
	currentInputs: VatReturnInputs;
}

export function CalculationsForm({
	setSelectedPage,
	setInputsInParent,
	currentInputs,
}: CalculationsFormProps) {
	const [searchParams] = useSearchParams();
	const { organisation } = useParams();
	const { form, inputs } = useCalculationForm(currentInputs.calculations);

	const filterParms: FilterDto = useMemo(() => {
		const pharmacyGroupName = searchParams.get('pharmacyGroupName');

		const filter: FilterDto = {
			// biome-ignore lint/style/noNonNullAssertion: <explanation>
			startDate: currentInputs.details!.startDate!,
			// biome-ignore lint/style/noNonNullAssertion: <explanation>
			endDate: currentInputs.details!.endDate!,
			pharmacyGroupName: pharmacyGroupName,
		};

		return filter;
	}, [inputs]);

	const grossSalesQuery = query<StatDto>(
		`${organisation}/vat/total-gross-sales?${buildFilterUrl(filterParms)}`,
	);

	useEffect(() => {
		form.setValues({
			vatDue: grossSalesQuery.data?.value.value,
		});
	}, [grossSalesQuery.data]);

	useEffect(() => {
		if (inputs.vatDue || inputs.vatDueOnAcquisitions) {
			const validVatDue = inputs.vatDue ?? 0;
			const validVatDueOnAcquistions = inputs.vatDueOnAcquisitions ?? 0;

			form.setFieldValue('totalVat', validVatDue + validVatDueOnAcquistions);
		}
	}, [inputs.vatDue, inputs.vatDueOnAcquisitions]);

	return (
		<form
			onSubmit={form.onSubmit(() => {
				form.validate();
			})}
		>
			<Grid>
				<Grid.Col span={12}>
					<NumberInput
						label={'VAT Due This Period'}
						placeholder="VAT due this period on sales and other outputs..."
						prefix="£ "
						allowNegative={false}
						decimalScale={2}
						fixedDecimalScale
						key={form.key('vatDue')}
						{...form.getInputProps('vatDue')}
					/>
				</Grid.Col>
				<Grid.Col span={12}>
					<NumberInput
						label={'VAT Due This Period On Acquisitions '}
						placeholder="VAT due in this period on acquisitions from other EC Member States..."
						prefix="£ "
						allowNegative={false}
						decimalScale={2}
						fixedDecimalScale
						key={form.key('vatDueOnAcquisitions')}
						{...form.getInputProps('vatDueOnAcquisitions')}
					/>
				</Grid.Col>
				<Grid.Col span={12}>
					<NumberInput
						label={'VAT Reclaimed'}
						placeholder="VAT reclaimed in this period on purchases and other inputs (including acquisitions from EC)..."
						prefix="£ "
						allowNegative={false}
						decimalScale={2}
						fixedDecimalScale
						key={form.key('vatReclaimed')}
						{...form.getInputProps('vatReclaimed')}
					/>
				</Grid.Col>
				<Grid.Col span={6}>
					<NumberInput
						label={'Total VAT'}
						placeholder="Total VAT due..."
						prefix="£ "
						allowNegative={false}
						decimalScale={2}
						fixedDecimalScale
						key={form.key('totalVat')}
						{...form.getInputProps('totalVat')}
					/>
				</Grid.Col>
				<Grid.Col span={6}>
					<NumberInput
						label={'VAT To Reclaim'}
						placeholder="VAT to reclaim from customs..."
						prefix="£ "
						allowNegative={false}
						decimalScale={2}
						fixedDecimalScale
						key={form.key('vatToReclaimFromCustoms')}
						{...form.getInputProps('vatToReclaimFromCustoms')}
					/>
				</Grid.Col>
			</Grid>
			<Space h={20} />
			<ButtonOrCancel
				onGoBack={() => setSelectedPage(0)}
				onClick={() => {
					if (form.isValid()) {
						setInputsInParent((prevInputs) => ({
							...prevInputs,
							calculations: inputs,
						}));
						setSelectedPage(2);
					}
				}}
			/>
		</form>
	);
}
